import { useMemo, useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { Card } from "../../../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Slider } from "../../../ui/slider";
import { Textarea } from "../../../ui/textarea";
import { toast } from "sonner";
import {
  useScoreDistribution,
  useScoreDistributionByScoring,
  useThreshold,
  useThresholdHistory,
  useModelRiskTier,
  useChallengerChartOptions,
  useChallengerScoreChart,
  useChallengerScoreCohorts,
  type ChallengerScoreChartRow,
  proposeThreshold as apiProposeThreshold,
  decideThreshold as apiDecideThreshold,
  type ThresholdHistoryEntry,
} from "../../mdm/lib/api/risk_appetite";
import { approvalRate } from "../../mdm/lib/risk-mock";
import { PageHeader } from "../../mdm/components/PageHeader";
import { StatCard } from "../../mdm/components/StatCard";

const PROPOSER = "Dan";
const APPROVER = "Mike";

type Role = "proposer" | "approver";

interface HistoryRow {
  id: string;
  status: "LIVE" | "SUPERSEDED" | "REJECTED" | "PENDING";
  cutoff: number;
  approvalRatePct: number;
  effectiveFrom: Date | null;
  effectiveTo: Date | null;
  proposedBy: string;
  proposedAt: Date;
  reason: string;
  decisionBy: string;
  decisionAt: Date;
  decisionNotes: string;
  kind: "approved" | "rejected";
}

function buildHistoryRowsFromApi(entries: ThresholdHistoryEntry[]): HistoryRow[] {
  return entries.map((e) => ({
    id: e.id,
    status: e.status as "LIVE" | "SUPERSEDED" | "REJECTED" | "PENDING",
    cutoff: e.threshold,
    approvalRatePct: e.approvalRatePct,
    effectiveFrom: e.effectiveFrom ? new Date(e.effectiveFrom) : null,
    effectiveTo: e.effectiveTo ? new Date(e.effectiveTo) : null,
    proposedBy: e.proposedBy || "—",
    proposedAt: new Date(e.proposedAt),
    reason: e.reason || "",
    decisionBy: e.decisionBy || "—",
    decisionAt: e.decisionAt ? new Date(e.decisionAt) : new Date(),
    decisionNotes: e.decisionNotes || "",
    kind: (e.status === "REJECTED" ? "rejected" : "approved") as "approved" | "rejected",
  }));
}

function formatDate(d: Date) {
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(d: Date) {
  return `${formatDate(d)} · ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

const SCORE_APPROVED_COLOR = "#38bdf8";
const SCORE_AVERTED_COLOR = "#1e40af";

const SCORING_TYPE_LABELS: Record<string, string> = {
  DPS: "Default Pass Score",
  FPS: "Fused Pass Score",
  JPS: "Joint Pass Score",
};

const ECON_LOSS_COLOR = "#22c55e";
const ECON_INTEREST_COLOR = "#ef4444";
const ECON_MARGIN_COLOR = "#eab308";
const ECON_PRICE_OF_RISK_COLOR = "#a855f7";

function safeRate(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}
function signAccent(v: number, goodWhenPositive: boolean): "green" | "red" | "default" {
  if (v === 0) return "default";
  return v > 0 === goodWhenPositive ? "green" : "red";
}
const fmtNum = (v: number) => Math.round(v).toLocaleString();
const fmtMoney = (v: number) => `${v < 0 ? "-" : ""}$${fmtNum(Math.abs(v))}`;
const fmtCompact = (v: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(v);
function passRate(r: ChallengerScoreChartRow): number {
  return safeRate(r.approvedScoreBased, r.totalApplications);
}
function predDefaults(r: ChallengerScoreChartRow): number {
  return r.totalDefaults - r.avertedChargeoffsScoreBased;
}
function defRate(r: ChallengerScoreChartRow): number {
  return safeRate(predDefaults(r), r.approvedScoreBased);
}

export function RiskAppetitePage() {
  const queryClient = useQueryClient();

  const { data: thresholdData } = useThreshold(2);
  const { data: historyData } = useThresholdHistory(2);

  const { data: scoreDistData } = useScoreDistribution(2);

  const [riskTierSegment, setRiskTierSegment] = useState<string>("OVERALL");
  const { data: riskTierData } = useModelRiskTier(riskTierSegment);

  const [scoreChartSegment, setScoreChartSegment] = useState<string>("OVERALL");
  const [scoreModelId, setScoreModelId] = useState<string>("");
  const [scoringType, setScoringType] = useState<string>("");
  const { data: chartOptions } = useChallengerChartOptions();
  const { data: scoreChartData } = useChallengerScoreChart(
    scoreChartSegment,
    scoreModelId,
    scoringType,
  );

  const [passSegment, setPassSegment] = useState<string>("OVERALL");
  const [passModelId, setPassModelId] = useState<string>("");
  const [passScoringType, setPassScoringType] = useState<string>("");

  const [marginSegment, setMarginSegment] = useState<string>("OVERALL");
  const [marginModelId, setMarginModelId] = useState<string>("");
  const [marginScoringType, setMarginScoringType] = useState<string>("");

  const [pipelineSegment, setPipelineSegment] = useState<string>("OVERALL");
  const [pipelineModelId, setPipelineModelId] = useState<string>("");
  const [pipelineScoringType, setPipelineScoringType] = useState<string>("");

  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (!chartOptions) return;
    const defaultScoring = chartOptions.scoringTypes.includes("JPS")
      ? "JPS"
      : chartOptions.scoringTypes[0];
    if (!scoreModelId && chartOptions.modelIds.length > 0) {
      setScoreModelId(chartOptions.modelIds[0]);
    }
    if (!scoringType && chartOptions.scoringTypes.length > 0) {
      setScoringType(defaultScoring);
    }
    if (!passModelId && chartOptions.modelIds.length > 0) {
      setPassModelId(chartOptions.modelIds[0]);
    }
    if (!passScoringType && chartOptions.scoringTypes.length > 0) {
      setPassScoringType(defaultScoring);
    }
    if (!marginModelId && chartOptions.modelIds.length > 0) {
      setMarginModelId(chartOptions.modelIds[0]);
    }
    if (!marginScoringType && chartOptions.scoringTypes.length > 0) {
      setMarginScoringType(defaultScoring);
    }
    if (!pipelineModelId && chartOptions.modelIds.length > 0) {
      setPipelineModelId(chartOptions.modelIds[0]);
    }
    if (!pipelineScoringType && chartOptions.scoringTypes.length > 0) {
      setPipelineScoringType(defaultScoring);
    }
  }, [
    chartOptions,
    scoreModelId,
    scoringType,
    passModelId,
    passScoringType,
    marginModelId,
    marginScoringType,
    pipelineModelId,
    pipelineScoringType,
  ]);

  const liveCutoff = thresholdData?.liveThreshold ?? 650;
  const pendingChange = thresholdData?.pendingChange ?? null;
  const lastUpdated = {
    at: new Date(thresholdData?.lastChangedAt ?? "2026-03-12T09:30:00"),
    by: thresholdData?.lastChangedBy ?? "—",
  };
  const population = scoreDistData?.population ?? [];

  const [role, setRole] = useState<Role>("proposer");
  const [proposed, setProposed] = useState<number>(650);

  const syncedRef = useRef(false);
  useEffect(() => {
    if (!syncedRef.current && thresholdData) {
      setProposed(thresholdData.liveThreshold);
      syncedRef.current = true;
    }
  }, [thresholdData]);

  const passThreshold = Math.round(proposed);
  const { data: passChartData } = useChallengerScoreChart(
    passSegment,
    passModelId,
    passScoringType,
  );
  const { data: passCohortsData } = useChallengerScoreCohorts(
    passSegment,
    passModelId,
    passScoringType,
    passThreshold,
  );

  const passChartRows = useMemo(
    () =>
      (passChartData?.rows ?? []).map((r) => ({
        threshold: r.threshold,
        passThroughRate: passRate(r) * 100,
        predictedDefaultRate: defRate(r) * 100,
      })),
    [passChartData],
  );

  const passSelRow = useMemo(
    () =>
      (passChartData?.rows ?? []).find((r) => r.threshold === passThreshold) ??
      null,
    [passChartData, passThreshold],
  );

  const livePassThroughRate = useMemo(() => {
    const row = (passChartData?.rows ?? []).find(
      (r) => r.threshold === Math.round(liveCutoff),
    );
    return row ? passRate(row) * 100 : null;
  }, [passChartData, liveCutoff]);

  const { data: marginChartData } = useChallengerScoreChart(
    marginSegment,
    marginModelId,
    marginScoringType,
  );
  const marginChartRows = useMemo(
    () =>
      (marginChartData?.rows ?? []).map((r) => ({
        threshold: r.threshold,
        lossAverted: r.netLossScoreBased,
        interestForegone: -r.interestEarnedScoreBased,
        marginImpact: r.netLossScoreBased - r.interestEarnedScoreBased,
        priceOfRisk:
          r.netLossScoreBased !== 0
            ? r.interestEarnedScoreBased / r.netLossScoreBased
            : null,
      })),
    [marginChartData],
  );

  const { data: pipelineDist } = useScoreDistributionByScoring(
    pipelineSegment,
    pipelineModelId,
    pipelineScoringType,
  );
  const bands = useMemo(() => {
    const bs = pipelineDist?.bands ?? [];
    const total = pipelineDist?.total || 1;
    return bs.map((b) => ({
      bin: `${b.lower}–${b.upper}`,
      low: b.lower,
      high: b.upper,
      count: b.count,
      pct: +((b.count / total) * 100).toFixed(2),
    }));
  }, [pipelineDist]);

  const sliderMin = chartOptions?.minThreshold ?? 450;
  const sliderMax = chartOptions?.maxThreshold ?? 900;
  const sliderTicks = [0, 0.25, 0.5, 0.75, 1].map((f) =>
    Math.round(sliderMin + (sliderMax - sliderMin) * f),
  );

  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeReason, setProposeReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNotes, setRejectNotes] = useState("");

  const [submittingPropose, setSubmittingPropose] = useState(false);
  const [submittingApprove, setSubmittingApprove] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "effectiveFrom", desc: true },
  ]);

  const approval = useMemo(() => approvalRate(population, proposed), [population, proposed]);
  const liveApproval = useMemo(() => approvalRate(population, liveCutoff), [population, liveCutoff]);

  const pendingApproval = pendingChange?.proposedApprovalRatePct ?? null;

  const canPropose =
    role === "proposer" &&
    !pendingChange &&
    Math.round(proposed) !== Math.round(liveCutoff);

  const proposeDisabledHint = pendingChange
    ? "A change is pending approval. Resolve it before proposing another."
    : role !== "proposer"
      ? "Switch to Proposer role to propose."
      : Math.round(proposed) === Math.round(liveCutoff)
        ? "Move the slider to propose a change."
        : "";

  async function handleSubmitProposal() {
    if (!proposeReason.trim()) return;
    setSubmittingPropose(true);
    try {
      await apiProposeThreshold({
        model_id: 2,
        proposedScore: proposed,
        reason: proposeReason.trim(),
        proposedBy: role === "approver" ? APPROVER : PROPOSER,
      });
      setProposeReason("");
      setProposeOpen(false);
      toast.success("Proposal submitted for approval");
      queryClient.invalidateQueries({ queryKey: ["threshold", 2] });
      queryClient.invalidateQueries({ queryKey: ["threshold-history", 2] });
    } catch {
      toast.error("Failed to submit proposal");
    } finally {
      setSubmittingPropose(false);
    }
  }

  async function handleApprove() {
    if (!approveNotes.trim() || !pendingChange) return;
    setSubmittingApprove(true);
    try {
      await apiDecideThreshold({
        model_id: 2,
        pendingId: pendingChange.id,
        decision: "APPROVE",
        decisionBy: APPROVER,
        decisionNotes: approveNotes.trim(),
      });
      setProposed(pendingChange.proposedScore);
      setApproveNotes("");
      setApproveOpen(false);
      toast.success(`Pass score ${Math.round(pendingChange.proposedScore)} is now live`);
      queryClient.invalidateQueries({ queryKey: ["threshold", 2] });
      queryClient.invalidateQueries({ queryKey: ["threshold-history", 2] });
      queryClient.invalidateQueries({ queryKey: ["score-distribution", 2] });
    } catch {
      toast.error("Failed to approve");
    } finally {
      setSubmittingApprove(false);
    }
  }

  async function handleReject() {
    if (!rejectNotes.trim() || !pendingChange) return;
    setSubmittingReject(true);
    try {
      await apiDecideThreshold({
        model_id: 2,
        pendingId: pendingChange.id,
        decision: "REJECT",
        decisionBy: APPROVER,
        decisionNotes: rejectNotes.trim(),
      });
      setRejectNotes("");
      setRejectOpen(false);
      toast("Proposal rejected");
      queryClient.invalidateQueries({ queryKey: ["threshold", 2] });
      queryClient.invalidateQueries({ queryKey: ["threshold-history", 2] });
    } catch {
      toast.error("Failed to reject proposal");
    } finally {
      setSubmittingReject(false);
    }
  }

  const selfApprovalBlocked =
    pendingChange != null &&
    role === "approver" &&
    pendingChange.proposedBy === APPROVER;

  const historyRows = useMemo(
    () => buildHistoryRowsFromApi(historyData?.history ?? []),
    [historyData],
  );

  const columnHelper = createColumnHelper<HistoryRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => {
          const s = info.getValue();
          if (s === "LIVE")
            return (
              <Badge className="bg-amber text-background border-transparent hover:bg-amber">
                LIVE
              </Badge>
            );
          if (s === "SUPERSEDED")
            return (
              <Badge
                variant="secondary"
                className="bg-muted text-muted-foreground border-transparent"
              >
                SUPERSEDED
              </Badge>
            );
          if (s === "PENDING")
            return (
              <Badge
                variant="outline"
                className="border-amber/60 text-amber"
              >
                PENDING
              </Badge>
            );
          return (
            <Badge
              variant="outline"
              className="border-destructive/60 text-destructive"
            >
              REJECTED
            </Badge>
          );
        },
        sortingFn: (a, b) => {
          const rank = { LIVE: 0, PENDING: 1, SUPERSEDED: 2, REJECTED: 3 } as const;
          return rank[a.original.status] - rank[b.original.status];
        },
      }),
      columnHelper.accessor("cutoff", {
        header: "Pass Score",
        cell: (info) => (
          <span className="tabular-nums">{Math.round(info.getValue())}</span>
        ),
      }),
      columnHelper.accessor("approvalRatePct", {
        header: "Approval Rate",
        cell: (info) => (
          <span className="tabular-nums">{info.getValue().toFixed(1)}%</span>
        ),
      }),
      columnHelper.accessor("effectiveFrom", {
        header: "Effective From",
        cell: (info) => {
          const v = info.getValue();
          return v ? (
            <span className="tabular-nums">{formatDate(v)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
        sortingFn: (a, b) => {
          const av = a.original.effectiveFrom?.getTime() ?? 0;
          const bv = b.original.effectiveFrom?.getTime() ?? 0;
          return av - bv;
        },
      }),
      columnHelper.accessor("effectiveTo", {
        header: "Effective To",
        cell: (info) => {
          const row = info.row.original;
          if (row.status === "LIVE")
            return <span className="text-amber">— current —</span>;
          if (row.status === "REJECTED")
            return <span className="text-muted-foreground">—</span>;
          return (
            <span className="tabular-nums">
              {row.effectiveTo ? formatDate(row.effectiveTo) : "—"}
            </span>
          );
        },
      }),
      columnHelper.accessor("proposedBy", {
        header: "Proposed By",
        cell: (info) => (
          <div className="text-xs">
            <div className="text-foreground/90">{info.getValue()}</div>
            <div className="text-muted-foreground tabular-nums">
              {formatDateTime(info.row.original.proposedAt)}
            </div>
          </div>
        ),
      }),
      columnHelper.accessor("reason", {
        header: "Reason",
        enableSorting: false,
        cell: (info) => (
          <div
            className="max-w-[220px] truncate text-xs text-foreground/80"
            title={info.getValue()}
          >
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor("decisionBy", {
        header: "Decision",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="text-xs">
              <div className="text-foreground/90">
                {row.kind === "approved" ? "Approved by " : "Rejected by "}
                {info.getValue()}
              </div>
              <div className="text-muted-foreground tabular-nums">
                {formatDateTime(row.decisionAt)}
              </div>
              <div
                className="mt-1 max-w-[220px] truncate text-muted-foreground"
                title={row.decisionNotes}
              >
                "{row.decisionNotes}"
              </div>
            </div>
          );
        },
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    data: historyRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <div className="pl-40 pr-64 pt-[175px] pb-24 space-y-[10px]">
        <div className="space-y-6">

          <PageHeader
            title="Risk Appetite"
            subtitle="Set the score at which applications pass. See the approval impact, then sign it off."
            right={
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                    Viewing as (demo)
                  </span>
                  <Select
                    value={role}
                    onValueChange={(v) => setRole(v as Role)}
                  >
                    <SelectTrigger className="h-8 w-[230px] border-border bg-card text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="proposer">
                        {PROPOSER} (Proposer)
                      </SelectItem>
                      <SelectItem value="approver">
                        {APPROVER} (Approver)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Demo control only production uses role-based access.
                </p>
              </div>
            }
          />

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Model"
              value={
                <div className="flex items-center gap-2">
                  <span>Challenger</span>
                  <Badge className="bg-amber-dim text-amber border-transparent hover:bg-amber-dim">
                    Champion
                  </Badge>
                </div>
              }
              sublabel={
                <>
                  Product type:{" "}
                  <span className="font-medium text-foreground/80">PG + Corp</span>
                </>
              }
            />
            <StatCard
              label="Current Pass Score"
              value={Math.round(liveCutoff)}
            />
            <StatCard
              label="Pass Through Rate at Live Threshold"
              value={
                livePassThroughRate != null
                  ? `${livePassThroughRate.toFixed(1)}%`
                  : `${liveApproval.toFixed(1)}%`
              }
            />
            <StatCard
              label="Last Updated"
              value={
                <span className="text-base font-medium">
                  {formatDate(lastUpdated.at)}
                </span>
              }
              sublabel={<>by {lastUpdated.by}</>}
            />
          </div>

          {pendingChange && pendingApproval != null && (
            <Card className="border-amber/60 bg-amber-dim/30 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-3">
                  <Badge className="bg-amber text-background border-transparent hover:bg-amber">
                    PENDING APPROVAL
                  </Badge>
                  <div className="flex items-center gap-3 text-2xl font-semibold tabular-nums">
                    <span>{Math.round(liveCutoff)}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="text-amber">
                      {Math.round(pendingChange.proposedScore)}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    Approval rate: {liveApproval.toFixed(1)}% →{" "}
                    <span className="text-foreground/80">
                      {pendingApproval.toFixed(1)}%
                    </span>
                  </div>
                  <div className="max-w-2xl text-sm text-foreground/90">
                    <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                      Reason
                    </span>
                    <p className="mt-1">{pendingChange.reason}</p>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Proposed by{" "}
                    <span className="text-foreground/80">
                      {pendingChange.proposedBy}
                    </span>{" "}
                    · {formatDateTime(new Date(pendingChange.proposedAt))}
                  </div>
                </div>

                {role === "approver" && (
                  <div className="flex flex-col items-end gap-2">
                    {selfApprovalBlocked ? (
                      <p className="text-xs text-muted-foreground max-w-[220px] text-right">
                        You proposed this change. A different person must approve.
                      </p>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="border-border text-foreground bg-transparent hover:bg-muted hover:border-amber transition-colors"
                          onClick={() => setRejectOpen(true)}
                        >
                          Reject
                        </Button>
                        <Button
                          className="bg-amber text-background hover:bg-amber/90"
                          onClick={() => setApproveOpen(true)}
                        >
                          Approve &amp; Make Live
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card className="border-border bg-card p-6 md:p-8">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Set Pass Score
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Drag to explore. The live pass score stays at{" "}
                  <span className="font-medium text-foreground/80">
                    {Math.round(liveCutoff)}
                  </span>{" "}
                  until a new value is signed off.
                </p>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Proposed Score
                </div>
                <div className="mt-1 text-5xl font-semibold tabular-nums text-amber">
                  {Math.round(proposed)}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <Slider
                value={[proposed]}
                min={sliderMin}
                max={sliderMax}
                step={1}
                onValueChange={(v) => setProposed(v[0])}
                className="[&_[role=slider]]:h-5 [&_[role=slider]]:w-5 [&_[role=slider]]:border-amber [&_[role=slider]]:bg-amber"
              />
              <div className="mt-2 flex justify-between text-[11px] tabular-nums text-muted-foreground">
                {sliderTicks.map((t, i) => (
                  <span key={i}>{t}</span>
                ))}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Raise the score threshold to approve fewer only stronger
                applications pass. Lower it to approve more.
              </p>
              <div className="flex flex-col items-end gap-1">
                <Button
                  disabled={!canPropose}
                  onClick={() => setProposeOpen(true)}
                  className="bg-amber text-background hover:bg-amber/90 disabled:bg-amber-dim disabled:text-muted-foreground disabled:opacity-100"
                >
                  Propose New Pass Score
                </Button>
                {!canPropose && proposeDisabledHint && (
                  <span className="text-[11px] text-muted-foreground">
                    {proposeDisabledHint}
                  </span>
                )}
              </div>
            </div>
          </Card>

          <Card className="border-border bg-card p-6 md:p-8">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Pass Through &amp; Predicted Default
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Pass-through and predicted-default rates across the threshold. Stat
                  cards and the table below reflect the pass score selected on the
                  slider ({passThreshold}).
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={passSegment} onValueChange={setPassSegment}>
                  <SelectTrigger className="w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OVERALL">OVERALL</SelectItem>
                    <SelectItem value="PG">PG</SelectItem>
                    <SelectItem value="CORP">CORP</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={passModelId} onValueChange={setPassModelId}>
                  <SelectTrigger className="w-36 text-xs">
                    <SelectValue placeholder="Model" />
                  </SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.modelIds ?? []).map((id) => (
                      <SelectItem key={id} value={id}>
                        {id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={passScoringType} onValueChange={setPassScoringType}>
                  <SelectTrigger className="w-28 text-xs">
                    <SelectValue placeholder="Scoring" />
                  </SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.scoringTypes ?? []).map((s) => (
                      <SelectItem key={s} value={s}>
                        {SCORING_TYPE_LABELS[s] ?? s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={340}>
              <LineChart
                data={passChartRows}
                margin={{ top: 8, right: 16, left: 8, bottom: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="threshold"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: "Passcore Threshold",
                    position: "insideBottom",
                    offset: -12,
                    fill: "var(--muted-foreground)",
                    fontSize: 11,
                  }}
                />
                <YAxis
                  yAxisId="left"
                  tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: "Pass Through Rate",
                    angle: -90,
                    position: "insideLeft",
                    fill: "var(--muted-foreground)",
                    fontSize: 11,
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tickFormatter={(v) => `${Number(v).toFixed(1)}%`}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  label={{
                    value: "Predicted Default Rate",
                    angle: 90,
                    position: "insideRight",
                    fill: "var(--muted-foreground)",
                    fontSize: 11,
                  }}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(2)}%`,
                    name,
                  ]}
                  labelFormatter={(label) => `Threshold ${label}`}
                  cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.4 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  formatter={(value) => (
                    <span style={{ color: "var(--muted-foreground)" }}>{value}</span>
                  )}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="passThroughRate"
                  name="Pass Through Rate"
                  stroke={SCORE_APPROVED_COLOR}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="predictedDefaultRate"
                  name="Predicted Default Rate"
                  stroke={SCORE_AVERTED_COLOR}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>

            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatCard
                label="Pass Through"
                value={passSelRow ? `${(passRate(passSelRow) * 100).toFixed(1)}%` : "—"}
                sublabel={
                  passSelRow ? (
                    <span className="tabular-nums">
                      {passSelRow.approvedScoreBased.toLocaleString()} approved
                    </span>
                  ) : undefined
                }
              />
              <StatCard
                label="Predicted Default"
                value={passSelRow ? `${(defRate(passSelRow) * 100).toFixed(1)}%` : "—"}
                sublabel={
                  passSelRow ? (
                    <span className="tabular-nums">
                      {predDefaults(passSelRow).toLocaleString()} defaults
                    </span>
                  ) : undefined
                }
              />
              <StatCard
                label="Loss Averted"
                value={passSelRow ? fmtMoney(passSelRow.netLossScoreBased) : "—"}
                accent={passSelRow ? signAccent(passSelRow.netLossScoreBased, true) : "default"}
              />
              <StatCard
                label="Interest Foregone"
                value={passSelRow ? fmtMoney(passSelRow.interestEarnedScoreBased) : "—"}
                accent={passSelRow ? signAccent(passSelRow.interestEarnedScoreBased, false) : "default"}
              />
              <StatCard
                label="Margin Impact"
                value={
                  passSelRow
                    ? fmtMoney(passSelRow.netLossScoreBased - passSelRow.interestEarnedScoreBased)
                    : "—"
                }
                accent={
                  passSelRow
                    ? signAccent(
                        passSelRow.netLossScoreBased - passSelRow.interestEarnedScoreBased,
                        true,
                      )
                    : "default"
                }
              />
              <StatCard
                label="Cost of Risk"
                value={
                  passSelRow && passSelRow.netLossScoreBased !== 0
                    ? `${(passSelRow.interestEarnedScoreBased / passSelRow.netLossScoreBased).toFixed(2)}x`
                    : "—"
                }
              />
            </div>

            <div className="mt-6 overflow-x-auto rounded-md border border-border">
              <table className="w-full caption-bottom text-sm">
                <thead className="bg-background/40">
                  <tr className="border-b border-border">
                    {[
                      "Cohort",
                      "Pass Through",
                      "Pass Through Rate",
                      "Predicted Defaults",
                      "Predicted Default Rate",
                      "Loss Averted",
                      "Interest Foregone",
                      "Margin Impact",
                    ].map((h) => (
                      <th
                        key={h}
                        className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-[0.14em] text-foreground"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(passCohortsData?.rows ?? []).map((r) => {
                    const ptRate = safeRate(r.approved, r.totalApplications) * 100;
                    const predDef = r.totalDefaults - r.avertedChargeoffs;
                    const pdRate = safeRate(predDef, r.approved) * 100;
                    return (
                      <tr
                        key={r.cohort}
                        className="border-b border-border last:border-0 hover:bg-muted/30"
                      >
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{r.cohort}</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{r.approved.toLocaleString()}</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{ptRate.toFixed(1)}%</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{predDef.toLocaleString()}</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{pdRate.toFixed(1)}%</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{fmtCompact(r.netLossScoreBased)}</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{fmtCompact(r.interestEarnedScoreBased)}</td>
                        <td className="px-3 py-3 align-middle text-sm tabular-nums">{fmtCompact(r.netLossScoreBased - r.interestEarnedScoreBased)}</td>
                      </tr>
                    );
                  })}
                  {(passCohortsData?.rows ?? []).length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted-foreground">
                        No data for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="border-border bg-card p-6 md:p-8">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Loss Averted vs Interest Foregone
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Loss averted, interest foregone and the net margin impact across the threshold.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={marginSegment} onValueChange={setMarginSegment}>
                  <SelectTrigger className="w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OVERALL">OVERALL</SelectItem>
                    <SelectItem value="PG">PG</SelectItem>
                    <SelectItem value="CORP">CORP</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={marginModelId} onValueChange={setMarginModelId}>
                  <SelectTrigger className="w-36 text-xs"><SelectValue placeholder="Model" /></SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.modelIds ?? []).map((id) => (
                      <SelectItem key={id} value={id}>{id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={marginScoringType} onValueChange={setMarginScoringType}>
                  <SelectTrigger className="w-28 text-xs"><SelectValue placeholder="Scoring" /></SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.scoringTypes ?? []).map((s) => (
                      <SelectItem key={s} value={s}>{SCORING_TYPE_LABELS[s] ?? s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={marginChartRows} margin={{ top: 8, right: 16, left: 8, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="threshold" type="number" domain={["dataMin", "dataMax"]}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false}
                  label={{ value: "Passcore Threshold", position: "insideBottom", offset: -12, fill: "var(--muted-foreground)", fontSize: 11 }}
                />
                <YAxis yAxisId="left" domain={[-20_000_000, 20_000_000]} allowDataOverflow
                  tickFormatter={(v) => fmtCompact(Number(v))}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false} width={72}
                />
                <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Number(v).toFixed(1)}x`}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false}
                  label={{ value: "Price of Risk", angle: 90, position: "insideRight", fill: "var(--muted-foreground)", fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(value: number, name: string) => name === "Price of Risk" ? [`${value.toFixed(2)}x`, name] : [Math.round(value).toLocaleString(), name]}
                  labelFormatter={(label) => `Threshold ${label}`}
                  cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.4 }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  formatter={(value) => <span style={{ color: "var(--muted-foreground)" }}>{value}</span>}
                />
                <ReferenceLine yAxisId="left" y={0} stroke="var(--muted-foreground)" strokeOpacity={0.5} />
                <Line yAxisId="left" type="monotone" dataKey="lossAverted" name="Loss Averted" stroke={ECON_LOSS_COLOR} strokeWidth={2} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="interestForegone" name="Interest Foregone" stroke={ECON_INTEREST_COLOR} strokeWidth={2} dot={false} />
                <Line yAxisId="left" type="monotone" dataKey="marginImpact" name="Margin Impact" stroke={ECON_MARGIN_COLOR} strokeWidth={2} dot={false} />
                <Line yAxisId="right" type="monotone" dataKey="priceOfRisk" name="Price of Risk" stroke={ECON_PRICE_OF_RISK_COLOR} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          <Card className="border-border bg-card p-6 md:p-8">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Pipeline by Score
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Application volume across the Athena score. Bars at or above the proposed pass score are approved; below are declined.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={pipelineSegment} onValueChange={setPipelineSegment}>
                  <SelectTrigger className="w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OVERALL">OVERALL</SelectItem>
                    <SelectItem value="PG">PG</SelectItem>
                    <SelectItem value="CORP">CORP</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={pipelineModelId} onValueChange={setPipelineModelId}>
                  <SelectTrigger className="w-36 text-xs"><SelectValue placeholder="Model" /></SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.modelIds ?? []).map((id) => (
                      <SelectItem key={id} value={id}>{id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={pipelineScoringType} onValueChange={setPipelineScoringType}>
                  <SelectTrigger className="w-28 text-xs"><SelectValue placeholder="Scoring" /></SelectTrigger>
                  <SelectContent>
                    {(chartOptions?.scoringTypes ?? []).map((s) => (
                      <SelectItem key={s} value={s}>{SCORING_TYPE_LABELS[s] ?? s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-2 h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bands} margin={{ top: 10, right: 16, left: 0, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="low" type="number" domain={[450, 900]} ticks={[450, 500, 550, 600, 650, 700, 750, 800, 850, 900]}
                    tickFormatter={(v) => String(Math.round(Number(v)))} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }}
                    label={{ value: "Athena Score", position: "insideBottom", offset: -12, fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }}
                    label={{ value: "Deals", angle: -90, position: "insideLeft", offset: 16, fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  />
                  <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }}
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, color: "hsl(var(--foreground))", fontSize: 12 }}
                    formatter={(value: number, _name, item) => [`${value} deals (${item.payload.pct}%)`, `${item.payload.bin}`]}
                    labelFormatter={() => ""}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {bands.map((b) => (
                      <Cell key={b.bin} fill={b.low >= proposed ? "var(--approve)" : "var(--decline)"} />
                    ))}
                  </Bar>
                  <ReferenceLine x={proposed} stroke="var(--amber)" strokeWidth={2} ifOverflow="extendDomain"
                    label={{ value: Math.round(proposed), position: "top", fill: "var(--amber)", fontSize: 11 }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-approve" />
                Approved (score ≥ pass score)
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-3 w-3 rounded-sm bg-decline" />
                Declined
              </span>
              <span className="inline-flex items-center gap-2 text-amber">
                <span className="inline-block h-2.5 w-[2px] bg-amber" />
                Amber line = proposed pass score
              </span>
            </div>
          </Card>

          <Card className="border-border bg-card p-6 md:p-8">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                  Autoscore Funded by Risk Tier
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Percentage of applications per risk tier, by booking year.
                </p>
              </div>
              <Select value={riskTierSegment} onValueChange={setRiskTierSegment}>
                <SelectTrigger className="w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OVERALL">OVERALL</SelectItem>
                  <SelectItem value="PG">PG</SelectItem>
                  <SelectItem value="CORP">CORP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={(riskTierData?.rows ?? []).filter((r) => Number(r.year) >= 2018)} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="year" tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `${Math.round(v)}%`} tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                  formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
                  cursor={{ fill: "hsl(var(--muted)/0.3)" }}
                />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  formatter={(value) => <span style={{ color: "var(--muted-foreground)" }}>{value}</span>}
                />
                <Bar dataKey="LOW"      stackId="a" fill="#22c55e" name="LOW"      radius={[0, 0, 0, 0]} />
                <Bar dataKey="MEDIUM"   stackId="a" fill="#eab308" name="MEDIUM"   radius={[0, 0, 0, 0]} />
                <Bar dataKey="HIGH"     stackId="a" fill="#f97316" name="HIGH"     radius={[0, 0, 0, 0]} />
                <Bar dataKey="CRITICAL" stackId="a" fill="#ef4444" name="CRITICAL" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {!showMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setShowMore(true)}
                className="border-border text-foreground bg-transparent hover:bg-muted hover:border-amber transition-colors">
                View More
              </Button>
            </div>
          )}

          {showMore && (<>
            <Card className="border-border bg-card p-6 md:p-8">
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                    Model Approved vs Defaults Averted
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Autoscore funded and defaults averted across the pass-score threshold, by product, model and scoring type.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={scoreChartSegment} onValueChange={setScoreChartSegment}>
                    <SelectTrigger className="w-28 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OVERALL">OVERALL</SelectItem>
                      <SelectItem value="PG">PG</SelectItem>
                      <SelectItem value="CORP">CORP</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={scoreModelId} onValueChange={setScoreModelId}>
                    <SelectTrigger className="w-36 text-xs"><SelectValue placeholder="Model" /></SelectTrigger>
                    <SelectContent>
                      {(chartOptions?.modelIds ?? []).map((id) => (
                        <SelectItem key={id} value={id}>{id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={scoringType} onValueChange={setScoringType}>
                    <SelectTrigger className="w-28 text-xs"><SelectValue placeholder="Scoring" /></SelectTrigger>
                    <SelectContent>
                      {(chartOptions?.scoringTypes ?? []).map((s) => (
                        <SelectItem key={s} value={s}>{SCORING_TYPE_LABELS[s] ?? s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={scoreChartData?.rows ?? []} margin={{ top: 8, right: 16, left: 8, bottom: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="threshold" type="number" domain={["dataMin", "dataMax"]}
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false}
                    label={{ value: "Passcore Threshold", position: "insideBottom", offset: -12, fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <YAxis yAxisId="left" tickFormatter={(v) => `${Math.round(Number(v) / 1000)}K`}
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false}
                    label={{ value: "Autoscore Funded", angle: -90, position: "insideLeft", fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${Math.round(Number(v) / 1000)}K`}
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} axisLine={false} tickLine={false}
                    label={{ value: "Defaults Averted", angle: 90, position: "insideRight", fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                    labelFormatter={(label) => `Threshold ${label}`}
                    cursor={{ stroke: "hsl(var(--muted-foreground))", strokeOpacity: 0.4 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                    formatter={(value) => <span style={{ color: "var(--muted-foreground)" }}>{value}</span>}
                  />
                  <Line yAxisId="left" type="monotone" dataKey="approvedScoreBased" name="Model Approved" stroke="#38bdf8" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="avertedChargeoffsScoreBased" name="Defaults Averted" stroke={SCORE_AVERTED_COLOR} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className="border-border bg-card p-6 md:p-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-amber">
                    Pass Score History
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Permanent audit trail of every proposed change approved and rejected.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-amber">
                  <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber" />
                  Amber = current live pass score
                </div>
              </div>

              <div className="mt-6 overflow-x-auto rounded-md border border-border">
                <table className="w-full caption-bottom text-sm">
                  <thead className="bg-background/40">
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id} className="border-b border-border">
                        {hg.headers.map((h) => {
                          const canSort = h.column.getCanSort();
                          const sorted = h.column.getIsSorted();
                          return (
                            <th key={h.id} className="h-10 px-3 text-left align-middle text-[11px] font-medium uppercase tracking-[0.14em] text-foreground">
                              {canSort ? (
                                <button type="button" onClick={h.column.getToggleSortingHandler()}
                                  className="inline-flex items-center gap-1 hover:text-amber/80 cursor-pointer">
                                  {flexRender(h.column.columnDef.header, h.getContext())}
                                  {sorted === "asc" ? <ArrowUp className="h-3 w-3" /> : sorted === "desc" ? <ArrowDown className="h-3 w-3" /> : <ChevronsUpDown className="h-3 w-3 opacity-50" />}
                                </button>
                              ) : flexRender(h.column.columnDef.header, h.getContext())}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => {
                      const isLive = row.original.status === "LIVE";
                      return (
                        <tr key={row.id} className={`border-b border-border last:border-0 ${isLive ? "bg-amber-dim/30" : "hover:bg-muted/30"}`}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="px-3 py-3 align-top text-sm">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex justify-center">
              <Button variant="outline" onClick={() => setShowMore(false)}
                className="border-border text-foreground bg-transparent hover:bg-muted hover:border-amber transition-colors">
                View Less
              </Button>
            </div>
          </>)}
        </div>

        {/* ── Propose dialog ── */}
        <Dialog open={proposeOpen} onOpenChange={(v) => { if (!submittingPropose) setProposeOpen(v); }}>
          <DialogContent className="p-0 !border-0 max-w-lg overflow-hidden [&>button:last-child]:text-white/70 [&>button:last-child]:hover:text-white">
            <div style={{ background: "hsl(218,30%,8%)", color: "hsl(30,9%,95%)", display: "flex", flexDirection: "column", fontFamily: "Inter,system-ui,sans-serif" }}>
              <div className="shrink-0 px-8 pt-8 pb-4" style={{ borderBottom: "1px solid hsla(216,11%,40%,0.35)" }}>
                <DialogTitle className="text-xl font-semibold mb-0" style={{ color: "hsl(33,98%,58%)" }}>
                  Propose Pass Score Change
                </DialogTitle>
                <DialogDescription className="mt-1 mb-0 text-sm font-medium text-[hsl(30,9%,95%)]/70">
                  Submit a proposed pass score for approval. A different person must approve before it goes live.
                </DialogDescription>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4" style={{ border: "1px solid hsl(216,11%,28%)", background: "hsla(216,11%,20%,0.4)" }}>
                    <div className="text-[11px] font-medium uppercase tracking-[0.14em]" style={{ color: "hsl(0,0%,55%)" }}>Current live</div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums">{Math.round(liveCutoff)}</div>
                    <div className="text-xs tabular-nums mt-1" style={{ color: "hsl(0,0%,55%)" }}>Approval {liveApproval.toFixed(1)}%</div>
                  </div>
                  <div className="p-4" style={{ border: "1px solid hsla(33,98%,58%,0.6)", background: "hsla(33,98%,58%,0.12)" }}>
                    <div className="text-[11px] font-medium uppercase tracking-[0.14em]" style={{ color: "hsl(33,98%,58%)" }}>Proposed</div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums" style={{ color: "hsl(33,98%,58%)" }}>{Math.round(proposed)}</div>
                    <div className="text-xs tabular-nums mt-1" style={{ color: "hsl(0,0%,55%)" }}>Approval {approval.toFixed(1)}%</div>
                  </div>
                </div>
                <p className="text-sm" style={{ color: "hsla(30,9%,95%,0.9)" }}>
                  {(() => {
                    const d = approval - liveApproval;
                    if (Math.abs(d) < 0.05) return "This would not meaningfully change approval volume.";
                    if (d < 0) return `This would decline an additional ${Math.abs(d).toFixed(1)}% of the pipeline.`;
                    return `This would approve an additional ${d.toFixed(1)}% of the pipeline.`;
                  })()}
                </p>
                <div>
                  <label className="text-[11px] font-medium uppercase tracking-[0.14em]" style={{ color: "hsl(33,98%,58%)" }}>
                    Reason for this change
                  </label>
                  <Textarea value={proposeReason} onChange={(e) => setProposeReason(e.target.value)}
                    placeholder="Explain why the pass score should change…"
                    className="mt-2 min-h-[100px] bg-[hsl(216,11%,14%)] border-[hsl(216,11%,28%)] text-[hsl(30,9%,95%)] placeholder:text-[hsl(0,0%,45%)]"
                  />
                </div>
              </div>
              <div className="shrink-0 px-8 pb-8 pt-4 flex justify-end gap-3" style={{ borderTop: "1px solid hsla(216,11%,40%,0.35)" }}>
                <button type="button" className="px-6 py-2 font-semibold min-w-[100px] transition-colors"
                  style={{ border: "1px solid hsl(216,11%,30%)", color: "hsl(30,9%,95%)", background: "transparent" }}
                  onClick={() => setProposeOpen(false)}>Cancel</button>
                <button type="button" disabled={!proposeReason.trim() || submittingPropose} onClick={handleSubmitProposal}
                  className="px-6 py-2 font-semibold min-w-[150px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "hsl(33,98%,58%)", color: "hsl(216,11%,15%)" }}>
                  {submittingPropose ? "Submitting…" : "Submit for Approval"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Approve dialog ── */}
        <Dialog open={approveOpen} onOpenChange={(v) => { if (!submittingApprove) setApproveOpen(v); }}>
          <DialogContent className="p-0 !border-0 max-w-lg overflow-hidden [&>button:last-child]:text-white/70 [&>button:last-child]:hover:text-white">
            <div style={{ background: "hsl(218,30%,8%)", color: "hsl(30,9%,95%)", display: "flex", flexDirection: "column", fontFamily: "Inter,system-ui,sans-serif" }}>
              <div className="shrink-0 px-8 pt-8 pb-4" style={{ borderBottom: "1px solid hsla(216,11%,40%,0.35)" }}>
                <DialogTitle className="text-xl font-semibold mb-0" style={{ color: "hsl(33,98%,58%)" }}>Approve &amp; Make Live</DialogTitle>
                <DialogDescription className="mt-1 mb-0 text-sm font-medium text-[hsl(30,9%,95%)]/70">
                  This will set the live pass score to{" "}
                  <span className="font-semibold" style={{ color: "hsl(30,9%,95%)" }}>
                    {pendingChange ? Math.round(pendingChange.proposedScore) : ""}
                  </span>{" "}immediately.
                </DialogDescription>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <label className="text-[11px] font-medium uppercase tracking-[0.14em]" style={{ color: "hsl(33,98%,58%)" }}>Approval notes</label>
                <Textarea value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)}
                  placeholder="Notes on this approval…"
                  className="mt-2 min-h-[100px] bg-[hsl(216,11%,14%)] border-[hsl(216,11%,28%)] text-[hsl(30,9%,95%)] placeholder:text-[hsl(0,0%,45%)]"
                />
              </div>
              <div className="shrink-0 px-8 pb-8 pt-4 flex justify-end gap-3" style={{ borderTop: "1px solid hsla(216,11%,40%,0.35)" }}>
                <button type="button" className="px-6 py-2 font-semibold min-w-[100px] transition-colors"
                  style={{ border: "1px solid hsl(216,11%,30%)", color: "hsl(30,9%,95%)", background: "transparent" }}
                  onClick={() => setApproveOpen(false)}>Cancel</button>
                <button type="button" disabled={!approveNotes.trim() || submittingApprove} onClick={handleApprove}
                  className="px-6 py-2 font-semibold min-w-[150px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "hsl(33,98%,58%)", color: "hsl(216,11%,15%)" }}>
                  {submittingApprove ? "Approving…" : "Approve & Make Live"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Reject dialog ── */}
        <Dialog open={rejectOpen} onOpenChange={(v) => { if (!submittingReject) setRejectOpen(v); }}>
          <DialogContent className="p-0 !border-0 max-w-lg overflow-hidden [&>button:last-child]:text-white/70 [&>button:last-child]:hover:text-white">
            <div style={{ background: "hsl(218,30%,8%)", color: "hsl(30,9%,95%)", display: "flex", flexDirection: "column", fontFamily: "Inter,system-ui,sans-serif" }}>
              <div className="shrink-0 px-8 pt-8 pb-4" style={{ borderBottom: "1px solid hsla(216,11%,40%,0.35)" }}>
                <DialogTitle className="text-xl font-semibold mb-0" style={{ color: "hsl(33,98%,58%)" }}>Reject Proposal</DialogTitle>
                <DialogDescription className="mt-1 mb-0 text-sm font-medium text-[hsl(30,9%,95%)]/70">
                  The proposed pass score will be discarded. The live pass score stays at{" "}
                  <span className="font-semibold" style={{ color: "hsl(30,9%,95%)" }}>{Math.round(liveCutoff)}</span>.
                </DialogDescription>
              </div>
              <div className="flex-1 overflow-y-auto px-8 py-6">
                <label className="text-[11px] font-medium uppercase tracking-[0.14em]" style={{ color: "hsl(33,98%,58%)" }}>Reason for rejection</label>
                <Textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                  placeholder="Why is this proposal being rejected?"
                  className="mt-2 min-h-[100px] bg-[hsl(216,11%,14%)] border-[hsl(216,11%,28%)] text-[hsl(30,9%,95%)] placeholder:text-[hsl(0,0%,45%)]"
                />
              </div>
              <div className="shrink-0 px-8 pb-8 pt-4 flex justify-end gap-3" style={{ borderTop: "1px solid hsla(216,11%,40%,0.35)" }}>
                <button type="button" className="px-6 py-2 font-semibold min-w-[100px] transition-colors"
                  style={{ border: "1px solid hsl(216,11%,30%)", color: "hsl(30,9%,95%)", background: "transparent" }}
                  onClick={() => setRejectOpen(false)}>Cancel</button>
                <button type="button" disabled={!rejectNotes.trim() || submittingReject} onClick={handleReject}
                  className="px-6 py-2 font-semibold min-w-[100px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "hsl(0,72%,51%)", color: "hsl(0,0%,100%)" }}>
                  {submittingReject ? "Rejecting…" : "Reject"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
